import React, { useEffect, useRef, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getVideoThumbnail } from '../../utils/videoUtils';

// Development-only logging helper
const devLog = () => {};

const devWarn = () => {};

const VideoPlayer = forwardRef(({ 
  lesson,
  onEnded,
  onPlay,
  onPause,
  onTimeUpdate,
  onLoadedMetadata,
  resumeTime = 0,
}, ref) => {

  const iframeRef = useRef(null);
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  // Detect video type and build appropriate URL
  const videoSource = useMemo(() => {
    if (!lesson) {
      devWarn('⚠️ [VideoPlayer] No lesson data');
      return null;
    }

    // Check if it's a YouTube URL
    if (lesson.video_url) {
      // YouTube URL patterns: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
      let youtubeId = null;
      
      // Pattern: youtube.com/watch?v=ID
      const youtubeMatch = lesson.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
      if (youtubeMatch && youtubeMatch[1]) {
        youtubeId = youtubeMatch[1];
        devLog('🎬 [VideoPlayer] Detected YouTube video:', youtubeId);
        return {
          type: 'youtube',
          url: `https://www.youtube.com/embed/${youtubeId}?enablejsapi=1`
        };
      }
    }

    // Check if it's an external URL (direct video file)
    if (lesson.video_url && !lesson.video_url.includes('iframe.mediadelivery.net')) {
      devLog('🎬 [VideoPlayer] Using external video URL:', lesson.video_url.substring(0, 80) + '...');
      return {
        type: 'html5',
        url: lesson.video_url
      };
    }

    // If video_url already contains full iframe embed, use it directly
    if (lesson.video_url && lesson.video_url.includes('iframe.mediadelivery.net/embed')) {
      devLog('🎬 [VideoPlayer] Using Bunny iframe embed:', lesson.video_url.substring(0, 80) + '...');
      return {
        type: 'bunny',
        url: lesson.video_url
      };
    }

    // Otherwise, try to construct Bunny iframe from bunny_video_id and library_id
    const videoId = lesson.bunny_video_id || lesson.video_id;
    const libraryId = lesson.bunny_library_id || lesson.library_id || 590142;

    if (videoId) {
      const params = new URLSearchParams();
      params.append('autoplay', 'false');
      params.append('preload', 'true');
      params.append('rememberPosition', 'true');
      
      if (lesson.lastWatchedPosition > 0) {
        params.append('t', Math.floor(lesson.lastWatchedPosition));
      }

      const url = `https://iframe.mediadelivery.net/embed/${libraryId}/${videoId}?${params.toString()}`;
      devLog('🎬 [VideoPlayer] Constructed Bunny iframe from IDs:', { libraryId, videoId });
      return {
        type: 'bunny',
        url
      };
    }

    devWarn('⚠️ [VideoPlayer] Missing video_url and video ID');
    return null;
  }, [lesson]);

  // Initialize Player.js API for Bunny videos, or native HTML5 for external videos
  useEffect(() => {
    // For external HTML5 videos
    if (videoSource?.type === 'html5' && videoRef.current) {
      const videoElement = videoRef.current;
      
      devLog('📹 [VideoPlayer] Using HTML5 video element');

      // Set resume time
      if (resumeTime > 0) {
        videoElement.currentTime = resumeTime;
        devLog('⏱️ [VideoPlayer] Resuming from:', resumeTime);
      }

      // Attach event listeners
      const handleLoadedMetadata = () => {
        devLog('⏱️ [VideoPlayer] Duration:', videoElement.duration, 'seconds');
        if (onLoadedMetadata) onLoadedMetadata(videoElement.duration);
      };

      const handlePlay = () => {
        devLog('▶️ [VideoPlayer] Playing');
        if (onPlay) onPlay();
      };

      const handlePause = () => {
        devLog('⏸️ [VideoPlayer] Paused');
        if (onPause) onPause();
      };

      const handleTimeUpdate = () => {
        if (onTimeUpdate) onTimeUpdate(videoElement.currentTime);
      };

      const handleEnded = () => {
        devLog('🏁 [VideoPlayer] Video ended');
        if (onEnded) onEnded();
      };

      const handleError = (e) => {
        // Video error handling
      };

      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('timeupdate', handleTimeUpdate);
      videoElement.addEventListener('ended', handleEnded);
      videoElement.addEventListener('error', handleError);

      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        videoElement.removeEventListener('ended', handleEnded);
        videoElement.removeEventListener('error', handleError);
      };
    }

    // For Bunny iframe videos
    if (videoSource?.type !== 'bunny' || !iframeRef.current || !videoSource?.url) return;

    // Capture iframe reference for use in cleanup function
    const iframeElement = iframeRef.current;

    devLog('📡 [VideoPlayer] Loading Player.js from Bunny CDN');

    // Load Player.js library from Bunny's official CDN
    const loadPlayerJS = () => {
      if (window.playerjs) {
        devLog('✅ [VideoPlayer] Player.js already loaded');
        initializePlayer();
        return;
      }

      const script = document.createElement('script');
      script.src = '//assets.mediadelivery.net/playerjs/playerjs-latest.min.js';
      script.async = true;
      script.type = 'text/javascript';

      script.onload = () => {
        devLog('✅ [VideoPlayer] Player.js loaded from Bunny CDN');
        // Small delay to ensure playerjs is initialized
        setTimeout(initializePlayer, 100);
      };

      script.onerror = () => {
        devWarn('⚠️ [VideoPlayer] Player.js failed to load - iframe controls will work but no progress API');
      };

      document.head.appendChild(script);
    };

    const initializePlayer = () => {
      if (!window.playerjs) {
        devWarn('⚠️ [VideoPlayer] playerjs not available after load');
        return;
      }

      if (!iframeRef.current) {
        devWarn('⚠️ [VideoPlayer] iframe ref lost');
        return;
      }

      try {
        // Create player instance from iframe
        const player = new window.playerjs.Player(iframeRef.current);
        playerRef.current = player;

        devLog('📹 [VideoPlayer] Player instance created');

        // When player is ready
        player.on('ready', () => {
          devLog('✅ [VideoPlayer] Player is ready');

          // Get initial duration
          player.getDuration((duration) => {
            devLog('⏱️ [VideoPlayer] Duration:', duration, 'seconds');
            if (onLoadedMetadata) {
              onLoadedMetadata({
                target: { duration, currentTime: 0 }
              });
            }
          });
        });

        // Handle play event
        player.on('play', () => {
          devLog('▶️ [VideoPlayer] Playing');
          onPlay?.();
        });

        // Handle pause event
        player.on('pause', () => {
          devLog('⏸️ [VideoPlayer] Paused');
          onPause?.();
        });

        // Handle ended event
        player.on('ended', () => {
          devLog('🏁 [VideoPlayer] Video ended');
          onEnded?.();
        });

        // Handle timeupdate - fires frequently during playback
        player.on('timeupdate', (data) => {
          const currentTime = data.seconds || 0;
          const duration = data.duration || 0;

          // Update ref for parent component
          if (ref && typeof ref === 'object') {
            ref.current = {
              currentTime,
              duration,
              paused: false,
            };
          }

          // Call parent callback with progress
          if (onTimeUpdate) {
            onTimeUpdate({
              target: {
                currentTime,
                duration,
              }
            });
          }
        });

        // Handle seeked event
        player.on('seeked', () => {
          devLog('⏩ [VideoPlayer] Seeked');
        });

        // Handle errors
        player.on('error', (error) => {
          // Player error handling
        });

        devLog('✅ [VideoPlayer] All event listeners attached');
      } catch (error) {
        // Failed to create player instance
      }
    };

    loadPlayerJS();

    return () => {
      try {
        // Check if iframe is still in the DOM
        if (!iframeElement || !iframeElement.parentElement) {
          devLog('🧹 [VideoPlayer] Iframe already removed from DOM');
          return;
        }

        if (playerRef.current && typeof playerRef.current.off === 'function') {
          try {
            playerRef.current.off('play');
            playerRef.current.off('pause');
            playerRef.current.off('ended');
            playerRef.current.off('timeupdate');
            playerRef.current.off('seeked');
            playerRef.current.off('error');
            playerRef.current.off('ready');
            devLog('🧹 [VideoPlayer] Cleaned up event listeners');
          } catch (e) {
            devWarn('⚠️ [VideoPlayer] Error removing listener:', e.message);
          }
        }
      } catch (error) {
        devWarn('⚠️ [VideoPlayer] Cleanup error:', error.message);
      }
    };

  }, [videoSource?.url, videoSource?.type, resumeTime, onEnded, onLoadedMetadata, onPause, onPlay, onTimeUpdate, ref]);

  // Fallback UI
  if (!videoSource) {
    return (
      <div className="aspect-video w-full rounded-2xl overflow-hidden bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg">❌ فيديو غير متاح</p>
          <p className="text-gray-400 text-sm mt-2">لم يتم العثور على معرف الفيديو أو المكتبة</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full rounded-2xl overflow-hidden bg-black shadow-2xl"
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        aspectRatio: '16 / 9',
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        return false;
      }}
      onDragStart={(e) => {
        e.preventDefault();
        return false;
      }}
    >
      {/* External video - HTML5 player */}
      {videoSource?.type === 'html5' && (
        <video
          ref={videoRef}
          controls
          controlsList="nodownload"
          poster={getVideoThumbnail(lesson.video_url, lesson)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        >
          <source src={videoSource.url} />
          متصفحك لا يدعم تشغيل الفيديو
        </video>
      )}

      {/* YouTube video */}
      {videoSource?.type === 'youtube' && (
        <iframe
          src={videoSource.url}
          style={{
            border: 'none',
            width: '100%',
            height: '100%',
            display: 'block',
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen={true}
          title={lesson?.title || 'فيديو YouTube'}
        />
      )}

      {/* Bunny iframe embed with Player.js API support */}
      {videoSource?.type === 'bunny' && (
        <iframe
          ref={iframeRef}
          src={videoSource.url}
          loading="lazy"
          style={{
            border: 'none',
            width: '100%',
            height: '100%',
            display: 'block',
          }}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen={true}
          sandbox="allow-same-origin allow-scripts allow-popups allow-presentation"
          title={lesson?.title || 'فيديو الدرس'}
        />
      )}

      {/* Watermark to prevent recording */}
      <div
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          color: 'rgba(255,255,255,0.2)',
          fontSize: '12px',
          pointerEvents: 'none',
          textAlign: 'right',
          fontFamily: 'system-ui',
          zIndex: 1,
        }}
      >
        <p>© 2026 جميع الحقوق محفوظة - منصتي التعليمية</p>
		</div>
    </div>
  );
});

VideoPlayer.displayName = 'VideoPlayer';

VideoPlayer.propTypes = {
  lesson: PropTypes.shape({
    bunny_video_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    library_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
  }),
  onEnded: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  resumeTime: PropTypes.number,
};

export default VideoPlayer;
